import { useState, useCallback, useEffect, useRef, Fragment, useMemo } from "react";
import { Database, Search, ArrowUp, ArrowDown, ChevronDown, Download } from "lucide-react";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import { useExplorerTables, useExplorerData } from "@/hooks/use-explorer";
import { apiFetch } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/utils";
import type { ExplorerTableInfo } from "@shared/types";

function abbreviateCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T|\s)/;

function renderCellValue(val: unknown): string {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (ISO_DATE_RE.test(s)) return formatDate(s);
  return s;
}

function escapeCsvCell(val: unknown): string {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(
  columns: string[],
  rows: Record<string, unknown>[]
): string {
  const header = columns.map(escapeCsvCell).join(",");
  const body = rows.map((row) =>
    columns.map((col) => escapeCsvCell(row[col])).join(",")
  );
  return [header, ...body].join("\n");
}

function downloadCsv(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function Explorer() {
  useSetPageHeader({
    title: "Data Explorer",
    subtitle: "Browse raw tables from the PlastiQ database",
    icon: Database,
  });

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const colPickerRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const tables = useExplorerTables();
  const data = useExplorerData(
    selectedSlug,
    page,
    50,
    debouncedSearch,
    sort,
    order
  );

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset state when table changes
  const selectTable = useCallback((slug: string) => {
    setSelectedSlug(slug);
    setPage(1);
    setSearch("");
    setDebouncedSearch("");
    setSort("");
    setOrder("desc");
    setHiddenCols(new Set());
    setColPickerOpen(false);
    setExpandedRow(null);
  }, []);

  // Default hidden columns for wide tables
  useEffect(() => {
    if (data.data && data.data.columns.length > 15) {
      const cols = data.data.columns;
      setHiddenCols(new Set(cols.slice(15)));
    }
  }, [data.data?.columns.length, selectedSlug]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        colPickerRef.current &&
        !colPickerRef.current.contains(e.target as Node)
      ) {
        setColPickerOpen(false);
      }
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(e.target as Node)
      ) {
        setExportMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSort = useCallback(
    (col: string) => {
      if (sort === col) {
        if (order === "desc") setOrder("asc");
        else {
          setSort("");
          setOrder("desc");
        }
      } else {
        setSort(col);
        setOrder("desc");
      }
      setPage(1);
    },
    [sort, order]
  );

  const toggleCol = useCallback((col: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  }, []);

  const exportPage = useCallback(() => {
    if (!data.data || !selectedSlug) return;
    const csv = buildCsv(data.data.columns, data.data.rows);
    downloadCsv(csv, `${selectedSlug}-page${page}.csv`);
    setExportMenuOpen(false);
  }, [data.data, selectedSlug, page]);

  const exportAll = useCallback(async () => {
    if (!selectedSlug) return;
    setExporting(true);
    setExportMenuOpen(false);
    try {
      const result = await apiFetch<{ columns: string[]; rows: Record<string, unknown>[] }>(
        `/api/explorer/data/${selectedSlug}`,
        { page: 1, limit: 10000, order: "asc" }
      );
      const csv = buildCsv(result.columns, result.rows);
      downloadCsv(csv, `${selectedSlug}-all.csv`);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  }, [selectedSlug]);

  // Build table info map for the selected table
  const tableInfoMap = new Map<string, ExplorerTableInfo>();
  if (tables.data) {
    for (const t of tables.data) tableInfoMap.set(t.slug, t);
  }
  const selectedInfo = selectedSlug
    ? tableInfoMap.get(selectedSlug)
    : undefined;

  const visibleCols =
    data.data?.columns.filter((c) => !hiddenCols.has(c)) ?? [];

  const [tableFilter, setTableFilter] = useState("");

  // Build domain groups from explorer table data
  const allDomains = useMemo(() => {
    const result: Record<string, { label: string; tables: ExplorerTableInfo[] }> = {};
    if (!tables.data) return result;
    for (const t of tables.data) {
      const domain = t.domain || "Other";
      if (!result[domain]) result[domain] = { label: domain, tables: [] };
      result[domain].tables.push(t);
    }
    return result;
  }, [tables.data]);

  // Filter table list by name
  const domains = useMemo(() => {
    if (!tableFilter.trim()) return allDomains;
    const q = tableFilter.toLowerCase();
    const filtered: typeof allDomains = {};
    for (const [key, group] of Object.entries(allDomains)) {
      const matching = group.tables.filter(
        (t: ExplorerTableInfo) => t.label.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)
      );
      if (matching.length > 0) {
        filtered[key] = { ...group, tables: matching };
      }
    }
    return filtered;
  }, [allDomains, tableFilter]);

  // Reset expanded row on page/search change
  useEffect(() => {
    setExpandedRow(null);
  }, [page, debouncedSearch, sort, order]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Table list — desktop sidebar */}
        <div className="hidden lg:block w-64 shrink-0">
          <div className="rounded-xl border border-border bg-card p-3 sticky top-4">
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Filter tables..."
                  value={tableFilter}
                  onChange={(e) => setTableFilter(e.target.value)}
                  className="w-full rounded-md border border-input bg-background pl-8 pr-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>
            {tables.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading tables...</p>
            ) : Object.keys(domains).length === 0 && tableFilter ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No tables match "{tableFilter}"</p>
            ) : (
              Object.entries(domains).map(([domain, group]) => (
                <div key={domain} className="mb-3 last:mb-0">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {group.label}
                  </p>
                  {group.tables.map((t: ExplorerTableInfo) => {
                    const isActive = selectedSlug === t.slug;
                    return (
                      <button
                        key={t.slug}
                        onClick={() => selectTable(t.slug)}
                        className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors ${
                          isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "text-muted-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                        }`}
                      >
                        <span className="truncate">{t.label}</span>
                        <span className="ml-2 text-xs tabular-nums opacity-60">
                          {abbreviateCount(t.row_count)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Table list — mobile dropdown */}
        <div className="lg:hidden">
          <select
            value={selectedSlug || ""}
            onChange={(e) =>
              e.target.value ? selectTable(e.target.value) : null
            }
            className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground"
          >
            <option value="">Select a table...</option>
            {Object.entries(domains).map(([domain, group]) => (
              <optgroup key={domain} label={group.label}>
                {group.tables.map((t: ExplorerTableInfo) => {
                  return (
                    <option key={t.slug} value={t.slug}>
                      {t.label} ({abbreviateCount(t.row_count)})
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Data panel */}
        <div className="flex-1 min-w-0">
          {!selectedSlug ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-12 text-center">
              <Database className="mb-3 h-12 w-12 text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">
                Select a table to browse
              </p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                {tables.data
                  ? `${tables.data.length} tables available across ${Object.keys(domains).length} domains`
                  : "Loading..."}
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-card p-5">
              {/* Header */}
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-card-foreground">
                    {selectedInfo?.label ?? selectedSlug}
                  </h2>
                  {selectedInfo && (
                    <p className="text-xs text-muted-foreground">
                      {formatNumber(selectedInfo.row_count)} rows,{" "}
                      {selectedInfo.column_count} columns
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-8 rounded-md border border-input bg-transparent pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                  </div>

                  {/* Column picker */}
                  {data.data && data.data.columns.length > 5 && (
                    <div className="relative" ref={colPickerRef}>
                      <button
                        onClick={() => setColPickerOpen(!colPickerOpen)}
                        className="flex h-8 items-center gap-1.5 rounded-md border border-input px-2.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        Columns ({visibleCols.length}/
                        {data.data.columns.length})
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {colPickerOpen && (
                        <div className="absolute right-0 top-full z-10 mt-1 max-h-64 w-56 overflow-y-auto rounded-md border border-border bg-popover p-2 shadow-md">
                          <div className="mb-2 flex gap-2">
                            <button
                              onClick={() => setHiddenCols(new Set())}
                              className="text-xs text-primary hover:underline"
                            >
                              Show all
                            </button>
                            <button
                              onClick={() =>
                                setHiddenCols(
                                  new Set(data.data!.columns.slice(15))
                                )
                              }
                              className="text-xs text-muted-foreground hover:underline"
                            >
                              Reset
                            </button>
                          </div>
                          {data.data.columns.map((col) => (
                            <label
                              key={col}
                              className="flex items-center gap-2 rounded px-1 py-0.5 text-xs text-foreground hover:bg-muted/50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={!hiddenCols.has(col)}
                                onChange={() => toggleCol(col)}
                                className="h-3 w-3"
                              />
                              <span className="truncate font-mono">
                                {col}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* CSV Export */}
                  {data.data && data.data.rows.length > 0 && (
                    <div className="relative" ref={exportMenuRef}>
                      <button
                        onClick={() => setExportMenuOpen(!exportMenuOpen)}
                        disabled={exporting}
                        className="flex h-8 items-center gap-1.5 rounded-md border border-input px-2.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                        {exporting ? "Exporting..." : "Export"}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      {exportMenuOpen && (
                        <div className="absolute right-0 top-full z-10 mt-1 w-44 rounded-md border border-border bg-popover p-1 shadow-md">
                          <button
                            onClick={exportPage}
                            className="flex w-full items-center rounded px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/50"
                          >
                            Export Page (CSV)
                          </button>
                          <button
                            onClick={exportAll}
                            className="flex w-full items-center rounded px-2.5 py-1.5 text-xs text-foreground hover:bg-muted/50"
                          >
                            Export All (CSV)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Data table */}
              {data.isLoading && !data.data ? (
                <div className="flex h-40 items-center justify-center text-muted-foreground">
                  Loading...
                </div>
              ) : data.data ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-left">
                          {visibleCols.map((col) => (
                            <th
                              key={col}
                              onClick={() => handleSort(col)}
                              className="cursor-pointer select-none whitespace-nowrap px-3 py-2 text-muted-foreground font-medium hover:text-foreground"
                            >
                              <span className="inline-flex items-center gap-1">
                                <span className="font-mono text-xs">
                                  {col}
                                </span>
                                {sort === col &&
                                  (order === "asc" ? (
                                    <ArrowUp className="h-3 w-3" />
                                  ) : (
                                    <ArrowDown className="h-3 w-3" />
                                  ))}
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.data.rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={visibleCols.length}
                              className="px-3 py-8 text-center text-muted-foreground"
                            >
                              {debouncedSearch
                                ? "No results match your search"
                                : "No data in this table"}
                            </td>
                          </tr>
                        ) : (
                          data.data.rows.map((row, i) => {
                            const isExpanded = expandedRow === i;
                            return (
                              <Fragment key={i}>
                                <tr
                                  className={`border-b border-border/50 cursor-pointer transition-colors ${isExpanded ? "bg-muted/70" : "hover:bg-muted/50"}`}
                                  onClick={() =>
                                    setExpandedRow(isExpanded ? null : i)
                                  }
                                >
                                  {visibleCols.map((col, ci) => {
                                    const val = row[col];
                                    const display = renderCellValue(val);
                                    const isNull =
                                      val === null || val === undefined;
                                    const isNumber =
                                      typeof val === "number" ||
                                      (!isNull &&
                                        !isNaN(Number(val)) &&
                                        String(val).trim() !== "" &&
                                        !ISO_DATE_RE.test(String(val)));
                                    return (
                                      <td
                                        key={col}
                                        className={`max-w-[200px] truncate whitespace-nowrap px-3 py-2 ${
                                          isNull
                                            ? "text-muted-foreground/40"
                                            : isNumber
                                              ? "text-right tabular-nums"
                                              : ""
                                        }`}
                                        title={display}
                                      >
                                        {ci === 0 && (
                                          <span className="mr-1.5 inline-block w-3 text-xs text-muted-foreground/60">
                                            {isExpanded ? "▾" : "▸"}
                                          </span>
                                        )}
                                        {isNull ? "—" : display}
                                      </td>
                                    );
                                  })}
                                </tr>
                                {isExpanded && (
                                  <tr className="border-b border-border/50">
                                    <td
                                      colSpan={visibleCols.length}
                                      className="px-3 py-3 bg-muted/30"
                                    >
                                      <RowDetail
                                        row={row}
                                        columns={data.data!.columns}
                                      />
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {data.data.total > data.data.limit && (
                    <div className="mt-4 flex items-center justify-between text-sm">
                      <p className="text-muted-foreground">
                        Showing{" "}
                        {(page - 1) * data.data.limit + 1}
                        –
                        {Math.min(page * data.data.limit, data.data.total)} of{" "}
                        {formatNumber(data.data.total)}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="rounded-md border border-input px-3 py-1 text-foreground disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setPage((p) => p + 1)}
                          disabled={
                            page * data.data.limit >= data.data.total
                          }
                          className="rounded-md border border-input px-3 py-1 text-foreground disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RowDetail({
  row,
  columns,
}: {
  row: Record<string, unknown>;
  columns: string[];
}) {
  // Show ALL columns vertically — no horizontal scroll needed
  const populated = columns.filter(
    (col) => row[col] !== null && row[col] !== undefined && String(row[col]).trim() !== ""
  );
  const empty = columns.filter(
    (col) => row[col] === null || row[col] === undefined || String(row[col]).trim() === ""
  );

  return (
    <div className="min-w-0 overflow-hidden">
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {populated.map((col) => {
          const val = row[col];
          const display = renderCellValue(val);
          return (
            <div key={col} className="min-w-0 py-0.5">
              <div className="text-[10px] font-mono text-muted-foreground/70 truncate" title={col}>
                {col}
              </div>
              <div className="text-xs text-foreground truncate" title={display}>
                {display}
              </div>
            </div>
          );
        })}
      </div>
      {empty.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground/50 break-words whitespace-normal">
          {empty.length} empty column{empty.length !== 1 ? "s" : ""}: {empty.join(", ")}
        </p>
      )}
    </div>
  );
}
